import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ConnectionStatus = ({ 
  isConnected = false,
  connectionHealth = 'good',
  lastConnected = null,
  tokenExpiry = null,
  onTestConnection,
  onDisconnect,
  isLoading = false,
  onRenew,
  isRenewing = false
}) => {
  const getStatusInfo = () => {
    if (!isConnected) {
      return {
        status: 'disconnected',
        label: 'No Conectado',
        description: 'Tu aplicación no está conectada con MercadoLibre',
        color: 'error',
        icon: 'XCircle'
      };
    }

    // Check if token is expired or expiring soon
    if (tokenExpiry) {
      const expiryDate = new Date(tokenExpiry);
      const now = new Date();
      const hoursUntilExpiry = (expiryDate - now) / (1000 * 60 * 60);

      if (hoursUntilExpiry < 0) {
        return {
          status: 'expired',
          label: 'Token Expirado',
          description: 'El token de acceso ha expirado. Reconecta para continuar',
          color: 'error',
          icon: 'Clock'
        };
      } else if (hoursUntilExpiry < 24) {
        return {
          status: 'expiring',
          label: 'Expirando Pronto',
          description: `Token expira en ${Math.floor(hoursUntilExpiry)} horas`,
          color: 'warning',
          icon: 'AlertTriangle'
        };
      }
    }

    return {
      status: 'connected',
      label: 'Conectado',
      description: 'Tu aplicación está conectada correctamente con MercadoLibre',
      color: 'success',
      icon: 'CheckCircle'
    };
  };

  const statusInfo = getStatusInfo();

  const formatDate = (dateString) => {
    if (!dateString) return 'Nunca';
    try {
      const date = new Date(dateString);
      return date?.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Fecha inválida';
    }
  };

  const getConnectionHealthColor = () => {
    switch (connectionHealth) {
      case 'excellent': return 'text-success';
      case 'good': return 'text-success';
      case 'fair': return 'text-warning';
      case 'poor': return 'text-error';
      default: return 'text-text-secondary';
    }
  };

  const getConnectionHealthLabel = () => {
    switch (connectionHealth) {
      case 'excellent': return 'Excelente';
      case 'good': return 'Buena';
      case 'fair': return 'Regular';
      case 'poor': return 'Mala';
      default: return 'Desconocida';
    }
  };

  return (
    <div className="bg-surface border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            statusInfo?.color === 'success' ? 'bg-success/10' :
            statusInfo?.color === 'warning' ? 'bg-warning/10' : 'bg-error/10'
          }`}>
            <Icon 
              name={statusInfo?.icon} 
              size={20} 
              className={`${
                statusInfo?.color === 'success' ? 'text-success' :
                statusInfo?.color === 'warning' ? 'text-warning' : 'text-error'
              }`} 
            />
          </div>
          <div>
            <h3 className="font-semibold text-text-primary">Estado de Conexión</h3>
            <p className="text-sm text-text-secondary">Monitorea tu integración con MercadoLibre</p>
          </div>
        </div>

        <div className="text-right">
          <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
            statusInfo?.color === 'success' ? 'bg-success/10 text-success' :
            statusInfo?.color === 'warning' ? 'bg-warning/10 text-warning' : 'bg-error/10 text-error'
          }`}>
            <Icon name={statusInfo?.icon} size={16} className="mr-2" />
            {statusInfo?.label}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-muted rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <Icon name="Wifi" size={20} className="text-primary" />
            <div>
              <p className="text-sm font-medium text-text-primary">Estado</p>
              <p className="text-xs text-text-secondary">{statusInfo?.description}</p>
            </div>
          </div>
        </div>

        <div className="bg-muted rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <Icon name="Clock" size={20} className="text-primary" />
            <div>
              <p className="text-sm font-medium text-text-primary">Última Conexión</p>
              <p className="text-xs text-text-secondary">{formatDate(lastConnected)}</p>
            </div>
          </div>
        </div>

        <div className="bg-muted rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <Icon name="Signal" size={20} className="text-primary" />
            <div>
              <p className="text-sm font-medium text-text-primary">Calidad</p>
              <p className={`text-xs ${getConnectionHealthColor()}`}>
                {getConnectionHealthLabel()}
              </p>
            </div>
          </div>
        </div>
      </div>
      {tokenExpiry && isConnected && (
        <div className="bg-muted/50 rounded-lg p-4 mb-6">
          <div className="flex items-start space-x-3">
            <Icon name="Calendar" size={18} className="text-primary mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-text-primary mb-1">Información del Token</p>
              <p className="text-xs text-text-secondary">
                Expira: {formatDate(tokenExpiry)}
              </p>
              {statusInfo?.status === 'expiring' && (
                <p className="text-xs text-warning mt-1">
                  ⚠️ Considera renovar la conexión pronto
                </p>
              )}
              {statusInfo?.status === 'expired' && (
                <p className="text-xs text-error mt-1">
                  ❌ Token expirado - Reconexión requerida
                </p>
              )}
            </div>
          </div>
        </div>
      )}
      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          variant="default"
          onClick={onTestConnection}
          loading={isLoading}
          iconName="Zap"
          iconPosition="left"
        >
          Probar Conexión
        </Button>
        
        {isConnected && (
          <Button
            variant="outline"
            onClick={onDisconnect}
            iconName="Unlink"
            iconPosition="left"
          >
            Desconectar
          </Button>
        )}

        {statusInfo?.status === 'expired' && (
          <Button
            variant="secondary"
            onClick={() => onRenew?.()}
            iconName="RefreshCw"
            iconPosition="left"
            loading={isRenewing}
          >
            Renovar Conexión
          </Button>
        )}
      </div>
      {!isConnected && (
        <div className="mt-4 p-3 bg-muted/30 rounded-lg text-sm text-text-secondary">
          <Icon name="Info" size={16} className="inline mr-2" />
          Para conectar, primero guarda tus credenciales de API y luego procede con la autorización OAuth.
        </div>
      )}
      {statusInfo?.status === 'expired' && (
        <div className="mt-4 p-3 bg-error/10 border border-error/20 rounded-lg text-sm text-error">
          <Icon name="AlertCircle" size={16} className="inline mr-2" />
          <strong>Token Expirado:</strong> Tu sesión con MercadoLibre ha vencido. 
          Necesitas reconectarte para continuar usando la API.
        </div>
      )}
    </div>
  );
};

export default ConnectionStatus;